import React, { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useGridApiRef } from '@mui/x-data-grid';
import * as yup from 'yup';
import dayjs from 'dayjs';

import {
  listChanceFails,
  setFailsFilters,
  resetFailsFilters,
} from './../../modules/chanceModule/chance-fails';
import { getNumberComma } from '../../utils/numberUtils';

// components
import renderCellExpand from '../../components/common/datagrid/render-cell-expand';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { ChanceFailListBox } from '../../components/chance-fail/ChanceFailListBox';
import ChanceFailStatusList from '../../components/chance-fail/ChanceFailStatusList';
import ChanceFailList from '../../components/chance-fail/ChanceFailList';
import CmpStatusChip from '../../components/common/datagrid/CmpStatusChip';

const ChanceFailListContainer = () => {
  const dispatch = useDispatch();
  const { chances, defaultFilters, error, loading, totals } = useSelector(
    ({ chanceModule, loading }) => ({
      chances: chanceModule.chanceFails.chances,
      totals: chanceModule.chanceFails.totals,
      defaultFilters: chanceModule.chanceFails.defaultFilters,
      error: chanceModule.chanceFails.error,
      loading: loading['chance/LIST_CHACE_FAILS'],
    }),
  );

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    const searchData = {
      prjTyp: filters.searchPrjTyp,
      prjImpt: filters.searchPrjImpt,
      cstGrade: filters.searchGrade,
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
      useYn: 'N',
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listChanceFails(makeFilters(defaultFilters)));
  }, [defaultFilters]);

  useEffect(() => {
    if (chances) {
      setTableData(chances);
    }
  }, [chances]);

  const handleFilters = (name, value) => {
    console.log(name, value);
    const newFilters = { ...defaultFilters, [name]: value };

    dispatch(setFailsFilters(newFilters));
  };

  const apiRef = useGridApiRef();

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'prjSeq',
        headerName: 'No',
        minWidth: 80,
        flex: 0.5,
        type: 'number',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'expDay',
        headerName: '매출월',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'prjImpt',
        headerName: '중요도',
        minWidth: 80,
        flex: 0.4,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'prjTyp',
        headerName: '타입',
        minWidth: 80,
        flex: 0.4,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value) =>
          value === 'M' ? '유지보수' : '프로젝트',
        renderCell: (params) => {
          return params.value === 'M' ? '유지보수' : '프로젝트';
        },
      },
      {
        field: 'vendorCmpNm',
        headerName: '매출처명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        editable: false,
        renderCell: (params) => params.row?.vendorCmpNm || '',
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 200,
        flex: 1,
        description: '',
        align: 'left',
        headerAlign: 'left',
        editable: false,
        renderCell: (params) => params.row?.cstNm || '',
      },
      {
        field: 'totalSales',
        headerName: '총매출액',
        minWidth: 120,
        flex: 0.6,
        description: '',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        valueFormatter: (value) => getNumberComma(value),
        renderCell: (params) => getNumberComma(params.value),
      },
      {
        field: 'totalPurchase',
        headerName: '총매입액',
        minWidth: 120,
        flex: 0.6,
        description: '',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        valueFormatter: (value) => getNumberComma(value),
        renderCell: (params) => getNumberComma(params.value),
      },
      {
        field: 'totalProfit',
        headerName: '매출이익',
        minWidth: 120,
        flex: 0.6,
        description: '',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        valueFormatter: (value) => getNumberComma(value),
        renderCell: (params) => getNumberComma(params.value),
      },
      {
        field: 'totalMargin',
        headerName: '이익률',
        minWidth: 90,
        flex: 0.4,
        description: '',
        align: 'center',
        headerAlign: 'center',
        editable: false,
      },
      {
        field: 'cmpStatus',
        headerName: '상태',
        minWidth: 150,
        flex: 0.8,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (!params.row) return null;
          return <CmpStatusChip value={params.row.cmpStatus} />;
        },
      },
      {
        field: 'failTxt',
        headerName: '실패사유',
        minWidth: 250,
        flex: 1,
        type: 'string',
        editable: false,
        renderCell: renderCellExpand,
      },
      {
        field: 'crtDptNm',
        headerName: '부서',
        minWidth: 120,
        flex: 0.6,
        type: 'string',
        editable: false,
      },
      {
        field: 'crtNm',
        headerName: '영업담당자',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        editable: false,
        valueGetter: (_, row) => {
          if (!row) return '';
          const crtNm = row.crtNm || '';
          const crtGradeNm = row.crtGradeNm || '';
          return `${crtNm} ${crtGradeNm}`.trim();
        },
        renderCell: (params) => {
          if (!params.row) return null;
          const crtNm = params.row.crtNm || '';
          const crtGradeNm = params.row.crtGradeNm || '';
          return (
            <div>
              {crtNm} {crtGradeNm}
            </div>
          );
        },
      },
      {
        field: 'prjNo',
        headerName: '프로젝트 번호',
        minWidth: 180,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'crtDt',
        headerName: '등록일자',
        minWidth: 180,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
    ],
    [],
  );

  let initialValues = {
    searchPrjImpt: defaultFilters.searchPrjImpt,
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchPrjImpt: yup.string().nullable(),
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '등록 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('등록 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        const searchData = {
          useYn: 'N',
          searchGrade: defaultFilters.searchGrade,
          searchPrjTyp: defaultFilters.searchPrjTyp,

          searchPrjImpt: values.searchPrjImpt,
          searchKeyword: values.searchKeyword,
          searchWords: values.searchWords,
          searchStrtDt: values.searchStrtDt,
          searchEndDt: values.searchEndDt,
        };
        dispatch(setFailsFilters(searchData));

        setSubmitting(false);
      }, 1000);
    },
  });

  return (
    <>
      <BreadcrumbsContainer
        title="포캐스팅 관리/수주 실패/리스트"
        icon="chance"
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <ChanceFailListBox formik={formik} onReset={() => dispatch(resetFailsFilters())} />
        <ChanceFailStatusList
          filters={defaultFilters}
          onFilters={handleFilters}
        />

        <ChanceFailList
          apiRef={apiRef}
          columns={columns}
          rows={tableData}
          error={error}
          loading={loading}
          columnVisibilityModel={{
            // Hide columns status and traderName, the other columns will remain visible
            id: false,
          }}
        />
      </Box>
    </>
  );
};

export default ChanceFailListContainer;
